import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";

const ACTION = {
  keep: ["Keep", "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"],
  remove: ["Remove", "bg-red-500/10 text-red-400 border-red-500/30"],
  review: ["Has zones \u2014 review", "bg-amber-500/10 text-amber-500 border-amber-500/30"],
};

export default function BikePathDuplicatesDialog({ open, onOpenChange, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setData(null); return; }
    api.get("/admin/trails/bike-paths/duplicates")
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(apiError(e.response?.data?.detail)); onOpenChange(false); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/trails/bike-paths/remove-duplicates");
      toast.success(`Removed ${r.removed} duplicate bike path${r.removed === 1 ? "" : "s"}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bikepath-duplicates-dialog">
        <DialogHeader>
          <DialogTitle>Duplicate bike paths</DialogTitle>
          <DialogDescription>Copies of the same trail. The copy with zones (or the one Trail Lab links to) is kept; extra copies with no zones are removed. Copies that have zones are never removed automatically.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : data.groups.length === 0 ? (
          <div className="rounded-lg border border-border p-3 text-sm flex items-center gap-2" data-testid="bikepath-duplicates-none"><CheckCircle2 className="w-4 h-4 text-emerald-400" />No duplicate bike paths found.</div>
        ) : (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            <div className="text-sm" data-testid="bikepath-duplicates-summary"><b>{data.removable}</b> to remove{data.needs_review ? <> · <b className="text-amber-500">{data.needs_review}</b> with zones to review</> : null}</div>
            {data.groups.map((g) => (
              <div key={g.key} className="rounded-lg border border-border" data-testid={`dup-group-${g.keep_id}`}>
                <div className="px-3 py-2 font-medium border-b border-border">{g.name} <span className="text-xs text-muted-foreground">· {g.copies.length} copies</span></div>
                {g.copies.map((r) => (
                  <div key={r.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`dup-copy-${r.id}`}>
                    <span className="text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : "unknown date"} · {r.zone_count} zone{r.zone_count === 1 ? "" : "s"}{r.linked ? " · linked to Trail Lab" : ""} · <span className="font-mono">{r.id.slice(-6)}</span></span>
                    <span className={`rounded px-1.5 py-0.5 border ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="bikepath-duplicates-close">Close</Button>
          <Button variant="destructive" onClick={apply} disabled={busy || !data?.removable} data-testid="bikepath-duplicates-apply">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Remove {data?.removable || 0} duplicate{data?.removable === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
